import { Icon } from 'gu-ui-kit'
import React, { lazy } from 'react'
import { ROUTES } from 'constants/routes'
import FinanceLayout from 'layouts/FinanceLayout'
import { AuthLayout } from 'layouts/AuthLayout'

const AccountsMarketplace = lazy(() => import('modules/AccountsMarketplace/Categories'))
const AccountsMarketplaceCategory = lazy(() => import('modules/AccountsMarketplace/Category'))
const AccountsMarketplaceProducts = lazy(() => import('modules/AccountsMarketplace/Products'))
const AccountsMarketplaceProduct = lazy(() => import('modules/AccountsMarketplace/Product'))

const AuthLogin = lazy(() => import('modules/Auth/Login'))
const AuthRegistration = lazy(() => import('modules/Auth/Registration'))
const AuthRecover = lazy(() => import('modules/Auth/Recover'))
const AuthRecoverSent = lazy(() => import('modules/Auth/RecoverSent'))
const AuthDoubleFA = lazy(() => import('modules/Auth/DoubleFA'))
const AuthAdmitadOauth = lazy(() => import('modules/Auth/AdmitadOauth'))
const AuthActivation = lazy(() => import('modules/Auth/Activation'))
const AuthRecoverPassword = lazy(() => import('modules/Auth/RecoverPassword'))

const ServicesList = lazy(() => import('modules/Services/NetworksList'))
const ServicesNetwork = lazy(() => import('modules/Services/Network'))
const ServicesNetworkAccount = lazy(() => import('modules/Services/Account'))
const ServicesNetworkAccountRefund = lazy(() => import('modules/Services/Refund'))
const ServicesNetworkAccountAdvertiserInfoEdit = lazy(() =>
  import('modules/Services/AccountAdvertiserInfoEdit'),
)
const ServicesNetworkAccountAccess = lazy(() => import('modules/Services/Access/List'))
const ServicesNetworkAccountAccessCreate = lazy(() => import('modules/Services/Access/Create'))
const ServicesNetworkAccountCampaigns = lazy(() => import('modules/Services/Campaigns/Main'))
const ServicesNetworkAccountCampaignCreate = lazy(() => import('modules/Services/Campaigns/Create'))
const ServicesNetworkAccountCampaignStart = lazy(() => import('modules/Services/Campaigns/Start'))
const ServicesNetworkAccountCreate = lazy(() => import('modules/Services/AccountCreate'))
const ServicesNetworkCampaignsEditorDefault = lazy(() =>
  import('modules/Services/CampaignsEditor/Default'),
)
const ServicesNetworkCampaignsEditor = lazy(() => import('modules/Services/CampaignsEditor'))

const Recommendations = lazy(() => import('modules/Recommendations'))

const RecommendationsWrapper = lazy(() =>
  import('modules/Recommendations').then((module) => ({
    default: module.RecommendationsWrapper,
  })),
)

const RecommendationsAudits = lazy(() => import('modules/Recommendations/Audits'))
const RecommendationsTypesList = lazy(() =>
  import('modules/Recommendations/CampaignsRecommendations/TypesList'),
)
const RecommendationsType = lazy(() =>
  import('modules/Recommendations/CampaignsRecommendations/Type'),
)

const AgencyCard = lazy(() => import('modules/Agency/AgencyCard'))
const Clients = lazy(() => import('modules/Agency/Clients'))
const Requests = lazy(() => import('modules/Agency/Requests'))
const AddClient = lazy(() => import('modules/Agency/AddClient'))
const Transfer = lazy(() => import('modules/Agency/Transfer'))

const ReferralList = lazy(() => import('modules/Referral/List'))
const ReferralPayout = lazy(() => import('modules/Referral/PayoutCreate'))
const ReferralInvoices = lazy(() => import('modules/Referral/PayoutProfiles/Invoices/Invoices'))
const PayoutProfiles = lazy(() => import('modules/Referral/PayoutProfiles/List'))
const PayoutProfilesCreate = lazy(() => import('modules/Referral/PayoutProfiles/Create'))

//const Pulse = lazy(() => import('modules/Statistics/Pulse'))
const StatisticsMain = lazy(() => import('modules/Statistics/Main'))
const StatisticsAccount = lazy(() => import('modules/Statistics/Account'))
const StatisticsAccountConnect = lazy(() => import('modules/Statistics/AccountConnect'))
const StatisticsCampaign = lazy(() => import('modules/Statistics/Campaign'))

const Settings = lazy(() => import('modules/User/Settings'))
const Password = lazy(() => import('modules/User/Security/Password'))
const DoubleFA = lazy(() => import('modules/User/Security/2fa'))
const DoubleFASetup = lazy(() => import('modules/User/Security/2fa/Setup'))

const AdvertisersList = lazy(() => import('modules/Advertisers/List'))
const AdvertiserCreate = lazy(() => import('modules/Advertisers/Create'))

const PaymentProfiles = lazy(() => import('modules/Finance/PaymentProfiles/List'))
const PaymentProfilesInvoices = lazy(() => import('modules/Finance/PaymentProfiles/Invoices'))
const PaymentProfilesCreate = lazy(() => import('modules/Finance/PaymentProfiles/Create'))

//Billing
const AccountReplenish = lazy(() => import('modules/Billing/AccountReplenish'))
const DepositReplenish = lazy(() => import('modules/Billing/DepositReplenish'))
const AccountsReplenish = lazy(() => import('modules/Billing/AccountsReplenish'))
const DepositCreationFee = lazy(() => import('modules/Billing/DepositCreationFee'))

const MarketplaceAccountReplenish = lazy(() =>
  import('modules/Billing/MarketplaceAccountReplenish'),
)

const PromisedPaymentRequest = lazy(() => import('modules/Finance/PromisedPayment'))
const AutoReplenish = lazy(() => import('modules/AutoReplenish'))
const OauthAuth = lazy(() => import('modules/Oauth/Auth'))
const OauthCode = lazy(() => import('modules/Oauth/Code'))

const Wallets = lazy(() => import('modules/Finance/Wallets'))

const PaymentsReturnUrl = lazy(() => import('modules/ReturnUrl/Payments'))
const TargetMailCode = lazy(() => import('modules/ReturnUrl/TargetMailCode'))
const VkCode = lazy(() => import('modules/ReturnUrl/VkCode'))
const VkAdsMarketCode = lazy(() => import('modules/ReturnUrl/VkAdsMarketCode'))
const VkAdsCode = lazy(() => import('modules/ReturnUrl/VkAdsCode'))

const TicketsList = lazy(() => import('modules/Tickets/List'))
const TicketsTicket = lazy(() => import('modules/Tickets/Ticket'))
const LocalPay = lazy(() => import('modules/LocalPay'))
const BillingPaymentOrder = lazy(() => import('modules/Billing/PaymentOrder'))
const ReferralClients = lazy(() => import('modules/Referral/ReferralClients'))
const ReferralLinks = lazy(() => import('modules/Referral/ReferralLinks'))
const ReferralLinksCreate = lazy(() => import('modules/Referral/ReferralLinks/CreateLink'))
const ReferralLinksEdit = lazy(() => import('modules/Referral/ReferralLinks/EditLink'))

const unAuthorizedRoutes = [
  {
    path: ROUTES.PAYMENT_ORDER,
    component: BillingPaymentOrder,
    exact: true,
    key: 'payment-order',
    layout: 'custom',
  },
  {
    path: ROUTES.AUTH_REGISTRATION,
    component: AuthRegistration,
    exact: true,
    key: 'registration',
    layout: AuthLayout,
  },
  {
    path: ROUTES.AUTH_LOGIN,
    component: AuthLogin,
    exact: true,
    key: 'login',
    layout: AuthLayout,
  },
  {
    path: ROUTES.AUTH_ACTIVATION,
    component: AuthActivation,
    exact: true,
    key: 'activation',
    layout: AuthLayout,
  },
  {
    path: ROUTES.AUTH_RECOVER_WITH_EMAIL,
    component: AuthRecover,
    exact: true,
    key: 'recover-with-email',
    layout: AuthLayout,
  },
  {
    path: ROUTES.AUTH_RECOVER,
    component: AuthRecover,
    exact: true,
    key: 'recover',
    layout: AuthLayout,
  },

  {
    path: ROUTES.AUTH_RECOVER_SENT,
    component: AuthRecoverSent,
    exact: true,
    key: 'recover-sent',
    layout: AuthLayout,
  },
  {
    path: ROUTES.AUTH_RECOVER_SET_PASSWORD,
    component: AuthRecoverPassword,
    exact: true,
    key: 'password-recover',
    layout: AuthLayout,
  },
  {
    path: ROUTES.AUTH_LOGIN_2FA,
    component: AuthDoubleFA,
    exact: true,
    key: '2fa',
    layout: AuthLayout,
  },
  {
    path: ROUTES.AUTH_ADMITAD_CHECK,
    component: AuthAdmitadOauth,
    exact: true,
    key: 'admitad-oauth',
  },
]

const authorizedRoutes = [
  {
    path: ROUTES.LOCAL_PAY,
    component: LocalPay,
    exact: true,
    key: 'local-pay',
  },
  {
    path: [
      ROUTES.QIWI_SUCCESS,
      ROUTES.QIWI_DECLINE,
      ROUTES.PAYMASTER_SERVICE_RESULT,
      ROUTES.PAYMENT_RESULT,
      ROUTES.BILLING_PAYMENT_RESULT,
    ],
    component: () => <PaymentsReturnUrl />,
    key: 'payment_return_url',
  },
  {
    path: ROUTES.AGENCY_ADD_CLIENT,
    exact: true,
    key: 'agency/add-client',
    icon: <Icon name="orders" size="m" />,
    label_key: 'agency::navigation.add-client',
    component: AddClient,
  },
  {
    path: ROUTES.TARGET_MAIL_CODE,
    component: () => <TargetMailCode />,
    key: 'target_mail_code_check',
  },
  {
    path: ROUTES.VK_CODE,
    component: () => <VkCode />,
    key: 'vk-code',
  },
  {
    path: ROUTES.ADSMARKET_CODE,
    component: () => <VkAdsMarketCode />,
    key: 'vk_code',
  },
  {
    path: ROUTES.VK_ADS_CODE,
    component: () => <VkAdsCode />,
    key: 'vk-ads-code',
  },
  {
    path: ROUTES.AUTOREPLENISH,
    exact: true,
    key: 'auto_replenish',
    label_key: 'common::nav_menu.auto_replenish',
    component: () => <AutoReplenish />,
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_REGISTER,
    exact: true,
    key: 'service_account_register',
    label_key: 'common::nav_menu.network_account_register',
    component: ServicesNetworkAccountCreate,
    layout: 'custom',
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_MULTIREGISTER,
    exact: true,
    key: 'service_account_register_multilogin',
    label_key: 'common::nav_menu.network_account_register',
    component: () => <ServicesNetworkAccountCreate multiLogins />,
    layout: 'custom',
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_ADVERTISER_INFO_EDIT,
    exact: true,
    key: 'service_network_account_tax_info_edit',
    label_key: 'common::nav_menu.network_account_tax_info_edit',
    component: ServicesNetworkAccountAdvertiserInfoEdit,
    layout: 'custom',
  },
  {
    path: [ROUTES.SERVICES_NETWORK_ACCOUNT_ACCESS_NEW, ROUTES.SERVICES_NETWORK_ACCOUNT_ACCESS_EDIT],
    exact: true,
    key: 'service_network_account_access_create',
    label_key: 'common::nav_menu.network_access_create',
    component: ServicesNetworkAccountAccessCreate,
    layout: 'custom',
  },
  {
    path: [ROUTES.ADVERTISER_NEW, ROUTES.ADVERTISER_EDIT],

    exact: true,
    key: 'advertisers/create',
    label_key: 'user::navigation.advertiser-create',
    component: AdvertiserCreate,
    icon: <Icon name="my-agency" size="m" />,
    layout: 'custom',
  },
  {
    path: [ROUTES.REFERRAL_PAYOUT_PROFILE_NEW, ROUTES.REFERRAL_PAYOUT_PROFILE_EDIT],

    exact: true,
    key: 'referral/payout-profiles-new',
    label_key: 'referral::navigation.payout-profiles-new',
    component: PayoutProfilesCreate,
    icon: <Icon name={'doc'} size={'m'} />,
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_REFUND,
    exact: true,
    key: 'service_network_account_refund',
    label_key: 'common::nav_menu.network_account_refund',
    component: ServicesNetworkAccountRefund,
    layout: 'custom',
  },
  {
    exact: true,
    path: ROUTES.REFERRAL_PAYOUT,
    key: 'referral/payout',
    label_key: 'referral::navigation.payout',
    component: ReferralPayout,
    icon: <Icon name={'user'} size={'m'} />,
  },
  {
    path: ROUTES.SERVICES,
    exact: true,
    key: 'services_list',
    label_key: 'common::nav_menu.advertisement_networks',
    component: ServicesList,
    icon: <Icon name="gramophone" size="m" />,
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT,
    exact: true,
    key: 'service_network_account',
    label_key: 'common::nav_menu.network_account',
    component: ServicesNetworkAccount,
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_ACCESS,
    exact: true,
    key: 'service_network_account_access_management',
    label_key: 'common::nav_menu.network_access_management',
    component: ServicesNetworkAccountAccess,
  },
  {
    path: ROUTES.SERVICES_NETWORK_CAMPAIGNS_EDITOR_DEFAULT,
    exact: true,
    key: 'service_campaigns_editor',
    label_key: 'services::campaigns_editor.title',
    component: ServicesNetworkCampaignsEditorDefault,
  },
  {
    path: ROUTES.SERVICES_NETWORK_CAMPAIGNS_EDITOR,
    key: 'service_campaigns_editor_resource',
    label_key: 'services::campaigns_editor.title',
    component: ServicesNetworkCampaignsEditor,
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_CAMPAIGNS,
    exact: true,
    key: 'service_network_account_campaigns',
    label_key: 'common::nav_menu.network_account_campaigns',
    component: ServicesNetworkAccountCampaigns,
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_CAMPAIGN_NEW,
    exact: true,
    key: 'service_network_account_campaign_create',
    label_key: 'common::nav_menu.network_account_campaign_create',
    component: ServicesNetworkAccountCampaignCreate,
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_CAMPAIGN_START,
    exact: true,
    key: 'service_network_account_campaign_start',
    label_key: 'common::nav_menu.network_account_campaign_start',
    component: ServicesNetworkAccountCampaignStart,
  },
  {
    path: ROUTES.SERVICES_NETWORK,
    exact: true,
    key: 'service_page',
    label_key: 'common::nav_menu.network',
    component: ServicesNetwork,
  },
  {
    path: ROUTES.STATISTICS,
    exact: true,
    label_key: 'statistics::navigation.main',
    key: 'statistics_main',
    component: StatisticsMain,
    icon: <Icon name={'statistics'} size="m" />,
  },
  {
    exact: true,

    path: ROUTES.STATISTICS_ACCOUNT,
    label_key: 'statistics::navigation.account',
    key: 'statistics_account',
    component: StatisticsAccount,
  },
  {
    exact: true,

    path: ROUTES.STATISTICS_CAMPAIGN,
    label_key: 'statistics::navigation.campaign',
    key: 'statistics_campaign',
    component: StatisticsCampaign,
  },
  {
    path: ROUTES.STATISTICS_ACCOUNT_CONNECT,
    exact: true,
    label_key: 'statistics::navigation.account_connect',
    key: 'statistics_account_connect',
    component: StatisticsAccountConnect,
  },
  {
    exact: true,
    path: ROUTES.ADVERTISERS,
    key: 'advertisers_list',
    label_key: 'user::navigation.advertisers',
    component: AdvertisersList,
    icon: <Icon name="user" size="m" />,
  },
  // {
  //   path: ROUTES.PULSE,
  //   exact: true,
  //   label_key: 'common::nav_menu.pulse',
  //   key: 'statistics_pulse',
  //   component: Pulse,
  //   icon: <Icon name="pulse" size="m" />,
  // },
  {
    path: ROUTES.RECOMMENDATIONS,
    key: 'recommendations',
    label_key: 'recommendations::navigation.main',
    component: Recommendations,
    icon: <Icon name="thumb-up" size="m" />,
    routes: [
      {
        hidden: true,
        exact: true,
        path: ROUTES.RECOMMENDATIONS,
        key: 'recommendations/redirect',
        component: RecommendationsWrapper,
        icon: <Icon name="thumb-up" size="m" />,
      },
      {
        exact: true,
        path: ROUTES.RECOMMENDATIONS_AUDITS,
        key: 'recommendations/audits',
        label_key: 'recommendations::navigation.audits',
        component: RecommendationsAudits,
        icon: <Icon name="thumb-up" size="m" />,
      },
      {
        exact: true,
        path: ROUTES.RECOMMENDATIONS_CAMPAIGNS,
        key: 'recommendations/campaigns',
        label_key: 'recommendations::navigation.main',
        component: RecommendationsTypesList,
        icon: <Icon name="thumb-up" size="m" />,
      },
      {
        exact: true,
        path: ROUTES.RECOMMENDATIONS_TYPE,
        key: 'recommendations/type',
        label_key: 'recommendations::navigation.type',
        component: RecommendationsType,
      },
    ],
  },
  {
    path: ROUTES.REFERRAL_LINKS_CREATE,
    exact: true,
    key: 'referral/links/create',
    label_key: 'referral::navigation.referral-links-create',
    component: ReferralLinksCreate,
    layout: 'custom',
  },
  {
    path: ROUTES.REFERRAL_LINKS_EDIT,
    exact: true,
    key: 'referral/links/edit',
    label_key: 'referral::navigation.referral-links-edit',
    component: ReferralLinksEdit,
    layout: 'custom',
  },
  {
    path: ROUTES.REFERRAL_LINKS,
    exact: true,
    key: 'referral/links',
    icon: <Icon name="copy-link" size="m" />,
    label_key: 'referral::navigation.referral-links',
    component: ReferralLinks,
  },
  {
    exact: true,
    path: ROUTES.REFERRAL,
    key: 'referral/main',
    label_key: 'referral::navigation.referral',
    component: ReferralList,
    icon: <Icon name={'user'} size={'m'} />,
  },
  {
    exact: true,
    path: ROUTES.REFERRALS,
    key: 'referrals/main',
    label_key: 'referral::navigation.referrals',
    component: ReferralList,
    icon: <Icon name={'user'} size={'m'} />,
  },
  {
    exact: true,
    path: ROUTES.REFERRAL_PAYOUT_PROFILES,
    key: 'referral/payout-profiles',
    label_key: 'referral::navigation.payout-profiles',
    component: PayoutProfiles,
    icon: <Icon name={'input'} size={'m'} />,
  },
  {
    exact: true,
    path: ROUTES.REFERRAL_PAYOUT_PROFILE_INVOICES,
    key: 'referral/payouts-list',
    label_key: 'referral::navigation.payouts-list',
    component: ReferralInvoices,
    icon: <Icon name={'money'} size={'m'} />,
  },
  {
    path: ROUTES.REFERRAL_CLIENTS,
    exact: true,
    key: 'referral/referral-clients',
    icon: <Icon name="users" size="m" />,
    label_key: 'referral::navigation.referral-clients',
    component: ReferralClients,
  },
  {
    exact: true,
    path: ROUTES.USER_SETTINGS,
    key: 'user/settings',
    label_key: 'user::navigation.settings',
    component: Settings,
    icon: <Icon name={'user'} size={'m'} />,
  },
  {
    exact: true,
    path: ROUTES.USER_SECURITY_PASSWORD,
    key: 'user/security/password',
    label_key: 'user::navigation.password',
    component: Password,
  },
  {
    exact: true,
    path: ROUTES.USER_SECURITY_2FA,
    key: 'user/security/2fa',
    label_key: 'user::navigation.2fa',
    component: DoubleFA,
  },
  {
    exact: true,
    path: ROUTES.USER_SECURITY_2FA_SETTING,
    key: 'user/security/2fa/setup',
    label_key: 'user::navigation.2fa',
    component: DoubleFASetup,
  },
  {
    layout: 'custom',
    exact: true,
    path: ROUTES.BILLING_TOP_UP,
    key: 'billing/top-up/deposit',
    label_key: 'billing::navigation.deposit',
    component: DepositReplenish,
  },
  {
    layout: 'custom',
    exact: true,
    path: ROUTES.BILLING_TOP_UP_ACCOUNT,
    key: 'billing/top-up/account',
    label_key: 'billing::navigation.account',
    component: AccountReplenish,
  },
  {
    layout: 'custom',
    exact: true,
    path: ROUTES.BILLING_TOP_UP_ACCOUNTS,
    key: 'billing/top-up/accounts',
    label_key: 'billing::navigation.deposit_accounts',
    component: AccountsReplenish,
  },
  {
    layout: 'custom',
    exact: true,
    path: ROUTES.BILLING_MARKETPLACE_PRODUCT_PAYMENT,
    key: 'billing/top-up/marketplace-account',
    label_key: 'billing::navigation.deposit_marketplace-account',
    component: MarketplaceAccountReplenish,
  },

  {
    layout: 'custom',
    exact: true,
    path: ROUTES.BILLING_DEPOSIT_CREATION_FEE,
    key: 'billing/deposit-creation-fee',
    label_key: 'billing::navigation.deposit_creation_fee',
    component: DepositCreationFee,
  },

  //FINANCE
  {
    layout: FinanceLayout,
    exact: true,
    path: ROUTES.FINANCE_WALLETS,
    key: 'finance/wallets',
    label_key: 'wallets::navigation.wallets',
    component: Wallets,
    icon: <Icon name="money" size="m" />,
  },
  {
    layout: FinanceLayout,
    exact: true,
    path: [ROUTES.FINANCE_WALLET, ROUTES.FINANCE_WALLET_PROMISED_PAYMENTS],
    key: 'finance/wallet',
    label_key: 'wallets::navigation.wallets',
    component: Wallets,
  },
  {
    layout: FinanceLayout,
    path: [ROUTES.FINANCE_PAYMENT_PROFILE_NEW, ROUTES.FINANCE_PAYMENT_PROFILE_EDIT],
    exact: true,
    key: 'finance/payment-profiles-new',
    label_key: 'user::navigation.payment-profiles-new',
    component: PaymentProfilesCreate,
    icon: <Icon name={'doc'} size={'m'} />,
  },
  {
    layout: FinanceLayout,
    exact: true,
    path: ROUTES.FINANCE_PAYMENT_PROFILES,
    key: 'finance/payment-profiles',
    label_key: 'user::navigation.payment-profiles',
    component: PaymentProfiles,
    icon: <Icon name={'user'} size={'m'} />,
  },
  {
    layout: FinanceLayout,
    exact: true,
    path: ROUTES.FINANCE_PAYMENT_PROFILE_INVOICES,
    key: 'finance/payment-profiles-invoices',
    label_key: 'user::navigation.payment-profiles-invoices',
    component: PaymentProfilesInvoices,
    icon: <Icon name={'doc'} size={'m'} />,
  },
  {
    layout: FinanceLayout,
    exact: true,
    label_key: 'common::nav_menu.promised_payment_request',
    key: 'finance/promised_payment_request',
    path: ROUTES.FINANCE_PROMISED_PAYMENT_REQUEST,
    component: PromisedPaymentRequest,
    icon: <Icon name={'orders'} size={'m'} />,
  },

  {
    path: ROUTES.OAUTH_CODE,
    exact: true,
    label_key: 'common::nav_menu.oauth_code',
    key: 'oauth_code',
    component: OauthCode,
  },
  {
    path: ROUTES.OAUTH_AUTH,

    exact: true,
    label_key: 'common::nav_menu.oauth_auth',
    key: 'oauth_auth',
    component: OauthAuth,
  },
  {
    layout: 'custom',
    exact: true,
    path: ROUTES.TICKETS,
    key: 'tickets/list',
    label_key: 'tickets::navigation.list',
    component: TicketsList,
  },
  {
    layout: 'custom',
    path: [ROUTES.TICKETS, ROUTES.TICKET_NEW, ROUTES.TICKET],
    exact: true,
    key: 'tickets/ticket',
    label_key: 'tickets::navigation.ticket',
    component: TicketsTicket,
  },
  {
    path: ROUTES.AGENCY_REQUESTS,
    exact: true,
    key: 'agency/requests',
    icon: <Icon name="comments" size="m" />,
    label_key: 'agency::navigation.requests',
    component: Requests,
  },
  {
    path: ROUTES.AGENCY_CLIENTS,
    exact: true,
    key: 'agency/clients',
    icon: <Icon name="user" size="m" />,
    label_key: 'agency::navigation.clients',
    component: Clients,
  },
  {
    path: ROUTES.AGENCY,
    exact: true,
    key: 'agency/main',
    icon: <Icon name="like" size="m" />,
    label_key: 'agency::navigation.my_agency',
    component: AgencyCard,
  },
  {
    path: ROUTES.AGENCY_TRANSFER,
    exact: true,
    key: 'agency/transfer',
    icon: <Icon name="clients" size="m" />,
    label_key: 'agency::navigation.transfer',
    component: Transfer,
  },

  //Accounts marketplace
  {
    exact: true,
    path: ROUTES.ACCOUNTS_MARKETPLACE,
    key: 'accounts-marketplace/list',
    label_key: 'accounts::navigation.marketplace',
    component: AccountsMarketplace,
    icon: <Icon name="user-add" size="m" />,
  },
  {
    path: ROUTES.ACCOUNTS_MARKETPLACE_CATEGORY,
    exact: true,
    key: 'accounts-marketplace/category',
    label_key: 'accounts::navigation.marketplace-category',
    component: AccountsMarketplaceCategory,
    icon: <Icon name="user-add" size="m" />,
  },
  {
    path: ROUTES.ACCOUNTS_MARKETPLACE_CATEGORY_PRODUCTS,
    exact: true,
    key: 'accounts-marketplace/category-products',
    label_key: 'accounts::navigation.marketplace-category-products',
    component: AccountsMarketplaceProducts,
    icon: <Icon name="user-add" size="m" />,
  },
  {
    path: ROUTES.ACCOUNTS_MARKETPLACE_PRODUCT,
    exact: true,
    key: 'accounts-marketplace/product',
    label_key: 'accounts::navigation.marketplace-category-product',
    component: AccountsMarketplaceProduct,
    icon: <Icon name="user-add" size="m" />,
  },
]

export default [...unAuthorizedRoutes, ...authorizedRoutes]
export { unAuthorizedRoutes as unAuthorizedRoutes }
export { authorizedRoutes as authorizedRoutes }
